import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, ReplaySubject, takeUntil, tap } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import UserUtil, { formatUserName } from 'app/core/utilities/user-util';
import { ModalSimpleMessageService } from 'app/shared/modals/modal-simple-message.service';
import { AppState } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import User from 'entity/User';
import { SocialEventType } from 'messages/websocket.messages';

import Message from '../../message/message';
import MessageType from '../../message/message-type';
import { BadgingService } from '../../services/badging/badging.service';
import { ChatService } from '../../services/chat.service';
import { HostService } from '../../services/host-service';
import { SocialMessageService } from '../../services/social-message.service';
import { VideoChatService } from '../../services/videochat.service';
import { Player } from '../../social-pixi-canvas/player';
import SocialStateHelper from '../../social-state-machine/social-state-helper';
import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppActions } from '../../state/app';
@Component({
  selector: 'app-guest-info',
  templateUrl: './guest-info.component.html',
  styleUrls: ['./guest-info.component.scss'],
  standalone: false,
})
export class GuestInfoComponent extends Loggable implements OnInit, OnDestroy {
  @Input() public guest?: Player;
  @Input() public even?: boolean;
  @Input() public isHost?: boolean;
  @Input() public social?: Social;

  public isGuestHost = false;
  public isGuestBanned = false;
  public isEventOn = false;
  private user?: User;
  public shouldDisplayGuestButtons = true;
  private destroyed$ = new ReplaySubject<boolean>(1);
  public guestName = '';
  protected smartBadgingEnabled = false;

  public constructor(
    private modalService: ModalSimpleMessageService,
    private hostService: HostService,
    private messageService: SocialMessageService,
    private videoChatService: VideoChatService,
    private chatService: ChatService,
    public badgingService: BadgingService,
    private store: Store<AppState>,
  ) {
    super();
    this.setDisplayName('GuestInfoComponent');
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.smart_badging)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((smartBadgingEnabled) => (this.smartBadgingEnabled = smartBadgingEnabled));
  }

  public ngOnInit(): void {
    this.shouldDisplayGuestButtons = this.guest ? this.user?.userId !== this.guest.user.userId : false;

    const { user } = this.guest ?? {};
    if (user) {
      this.guestName = UserUtil.trimName(20, formatUserName(user));
    }

    SocialStateMachine.shared.stateChanged$.pipe(takeUntil(this.destroyed$)).subscribe((state) => {
      if (this.guest) {
        this.isGuestHost = SocialStateHelper.isHost(state.hosts, this.guest.user.userId ?? '', this.social);
        this.isGuestBanned = SocialStateHelper.isBannedUser(
          Object.keys(state.readOnly.bannedUsers),
          this.guest.user.userId ?? '',
        );
      }
      this.isEventOn = state.eventType === SocialEventType.CONVO || state.eventType === SocialEventType.TOAST;
      if (this.isEventOn) {
        this.modalService.close();
      }
    });
  }
  public openBadge = () => {
    const { userId } = this.guest?.user ?? {};
    this.store.dispatch(socialAppActions.TOGGLE_BADGE_OPEN({ open: true, userId: userId ? userId : '' }));
  };

  public sendMessage(): void {
    const { userId } = this.guest?.user ?? {};
    if (!userId) {
      this.error('sendMessage: userId is not defined', { guest: this.guest });
      return;
    }
    this.chatService.triggerPrivateMessage(userId);
  }

  public addHost(): void {
    const name = `${this.guest?.user.firstName} ${this.guest?.user.lastName || ''}`;
    this.modalService
      .confirm({
        title: `Are you sure you want to make ${name} a host?`,
        message: `This person will temporarily be a host until the room is closed.`,
        actionButtonTitle: 'MAKE HOST',
        showDoneButton: true,
      })
      .pipe(
        filter((result) => result),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        if (this.isEventOn) {
          return;
        }

        assertDefined(this.user?.userId);
        this.hostService.addHost(
          this.user.userId,
          this.guest?.user.userId,
          this.social?.socialId,
          this.guest?.user.firstName,
          this.guest?.user.lastName,
        );
        const message: Message = {
          type: MessageType.NEW_HOST,
          message: 'has made you a host.',
          firstName: this.user?.firstName,
          lastName: this.user?.lastName,
          userId: this.guest?.user.userId,
        };
        this.messageService.sendMessage(message);
      });
  }

  public removeFromRoom(): void {
    this.modalService
      .confirm({
        title: `Are you sure you want to remove
      ${this.guest?.user.firstName} ${this.guest?.user.lastName || ''} from this Room?`,
        message: `This person will be kicked out of the room.`,
        actionButtonTitle: 'REMOVE',
        showDoneButton: true,
      })
      .pipe(
        filter((result) => result),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        if (this.isEventOn) {
          return;
        }
        const { socialId } = this.social ?? {};
        const { userId: guestUserId } = this.guest?.user ?? {};
        if (!socialId || !guestUserId) {
          return;
        }
        void this.videoChatService.setAdmittedStatus(socialId, guestUserId, false, true);
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
